import axios from 'axios';
import { toast } from 'react-toastify';
import React from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import { tBackend } from 'locales/i18n';
import {
  SmartphoneNfc,
  ShieldAlert,
  MessageSquareOff,
  Phone,
  PhoneCall,
} from 'lucide-react';

const shouldShowSuccessToast = () => {
  if (typeof localStorage === 'undefined') return true;
  const value = localStorage.getItem('showSuccessToast');
  return value !== 'false';
};

const shouldShowErrorToast = () => {
  if (typeof localStorage === 'undefined') return true;
  const value = localStorage.getItem('showErrorToast');
  return value !== 'false';
};
// import * as Sentry from '@sentry/react';

export const successModal = (text: string) => {
  if (!shouldShowSuccessToast()) return;
  toast.success(tBackend(text), {
    position: 'top-center',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    style: {
      whiteSpace: 'pre-line',
      textAlign: 'left',
    },
  });
};

export const warningModal = (text: string) => {
  toast.warning(tBackend(text), {
    position: 'top-center',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    style: {
      whiteSpace: 'pre-line',
      textAlign: 'left',
    },
  });
};

// Error codes that should be silently ignored (no toast)
const SILENT_ERROR_CODES = ['131026', '131049'];

export const errorModal = async (text: string, msg?: string) => {
  if (!shouldShowErrorToast()) return;
  // Skip toast for specific error codes
  if (SILENT_ERROR_CODES.some(code => text.includes(code))) {
    return;
  }
  showToastError(text, msg);
  // sentryLogError(text, { message: msg ?? '' });
};

export function receiveError(error: unknown, msg?: string) {
  if (!shouldShowErrorToast()) return;
  console.error(error);
  let errorMessage = i18next.t('An unknown error occurred.');
  if (axios && axios.isAxiosError && axios.isAxiosError(error)) {
    const serverResponse = error.response;
    let data = serverResponse?.data || error.message;
    // Backend CustomError payload: translate the human-facing message field,
    // leave errorType/errorRaw untouched.
    if (data && typeof data === 'object') {
      const d = data as { errorMessage?: unknown; message?: unknown };
      data = {
        ...data,
        ...(typeof d.errorMessage === 'string' && {
          errorMessage: tBackend(d.errorMessage),
        }),
        ...(typeof d.message === 'string' && { message: tBackend(d.message) }),
      };
    }
    errorMessage = JSON.stringify(data, null, 2);
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error && typeof error === 'object') {
    errorMessage = JSON.stringify(error, null, 2);
  }
  errorModal(errorMessage, msg);
}

type CustomToastProps = {
  jsonText: string;
  msg?: string;
};

export const CustomToast: React.FC<CustomToastProps> = ({ jsonText, msg }) => {
  const renderJson = (jsonObj: any): React.JSX.Element[] => {
    return Object.entries(jsonObj).map(([key, value]) => {
      // Check if the value is an object and not an array or null
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return (
          <div key={key}>
            <span
              style={{
                fontWeight: 'bold',
                color: 'var(--heltar-text-link-hover)',
              }}
            >
              {key}
            </span>
            :<div style={{ marginLeft: 20 }}>{renderJson(value)}</div>
          </div>
        );
      } else {
        // Handle non-object values (e.g., strings, numbers)
        return (
          <div key={key}>
            <span
              style={{
                fontWeight: 'bold',
                color: 'var(--heltar-text-link-hover)',
              }}
            >
              {key}
            </span>
            : {JSON.stringify(value)}
          </div>
        );
      }
    });
  };
  const renderContent = (): React.JSX.Element[] => {
    const renderedElements: React.JSX.Element[] = [];
    if (msg) renderedElements.push(<div key={nanoid()}>{msg}</div>);
    try {
      const jsonObj = JSON.parse(jsonText);
      if (typeof jsonObj === 'object' && jsonObj !== null) {
        renderedElements.push(
          <div style={{ textAlign: 'left' }} key={nanoid()}>
            {renderJson(jsonObj)}
          </div>,
        );
      } else {
        renderedElements.push(
          <div key={nanoid()}>
            {typeof jsonObj === 'string' ? tBackend(jsonObj) : jsonObj}
          </div>,
        );
      }
    } catch (error) {
      console.error('Error parsing JSON:', error);
      renderedElements.push(<p key={nanoid()}>{tBackend(jsonText)}</p>);
    }
    return renderedElements;
  };

  return <div key={nanoid()}>{renderContent()}</div>;
};

const showToastError = async (text: string, msg?: string) => {
  if (!shouldShowErrorToast()) return;
  toast.error(<CustomToast jsonText={text} msg={msg} />, {
    position: 'top-center',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export const toastUpdate = (
  fileName: string,
  percentageUpload: number,
  toastId: string | number,
) => {
  toast.update(toastId, {
    progress: percentageUpload / 100, // progress as a fraction
    render: (
      <div>
        {i18next.t('Uploading file name {{fileName}}.', { fileName })}
        <br />
        <span
          style={{ color: 'var(--heltar-text-link-hover)', fontWeight: 'bold' }}
        >
          {i18next.t('Uploading... {{percentageUpload}}%', {
            percentageUpload,
          })}
        </span>
      </div>
    ),
  });
};

export const Error131026 = ({
  clientWaNumber,
}: {
  clientWaNumber?: string;
}): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className='space-y-2.5 py-1'>
      <p className='font-semibold text-[13px]'>{t('Message not delivered')}</p>
      <div className='space-y-1.5'>
        <div className='flex items-start gap-2'>
          <SmartphoneNfc className='w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--heltar-state-error-medium)]' />
          <span className='text-xs'>{t('Number is not on WhatsApp')}</span>
        </div>
        <div className='flex items-start gap-2'>
          <ShieldAlert className='w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--heltar-state-error-medium)]' />
          <span className='text-xs'>
            {t('Old WhatsApp version or T&C not accepted')}
          </span>
        </div>
        <div className='flex items-start gap-2'>
          <MessageSquareOff className='w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--heltar-state-error-medium)]' />
          <span className='text-xs'>
            {t('Too many marketing messages — blocked by WhatsApp')}
          </span>
        </div>
        {clientWaNumber && (
          <div className='flex items-start gap-2'>
            <Phone className='w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--heltar-text-secondary)]' />
            <span className='text-xs'>{clientWaNumber}</span>
          </div>
        )}
      </div>
      <div className='flex items-start gap-2 pt-1 border-t border-[var(--heltar-tooltip-border)]'>
        <PhoneCall className='w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--heltar-brand-primary)]' />
        <span className='text-xs'>
          {t(
            'Call the customer and ask them to message you first, or try again later.',
          )}
        </span>
      </div>
    </div>
  );
};

// const sentryLogError = (
//   error: Error | string,
//   context?: { message: string },
// ): void => {
//   Sentry.withScope(scope => {
//     if (context) {
//       scope.setExtra('contextMessage', context.message);
//     }
//     let errorObject = error;
//     if (typeof error === 'string') {
//       try {
//         errorObject = JSON.parse(error);
//       } catch (parseError) {
//         errorObject = new Error(error);
//       }
//     }
//     Sentry.captureException(errorObject);
//   });
// };
